import { useState, useMemo, useEffect } from "react";
import ButtonIconH from "./ButtonIconH";
import InputData from "./InputData";
import { focusElement } from "../js/utils";
import moment from "moment";

const LOTE_TAB_BASE = 13;

export default function CRMScheduledCampaignSend({ total, onAccept }) {
  const [data, setData] = useState([]);
  const [numBatches, setNumBatches] = useState(1);
  const totalAsignado = useMemo(
    () => data.reduce((sum, item) => sum + item.numEnvios, 0),
    [data],
  );
  const pendiente = total - totalAsignado;

  const formatDate = (date) => date.toISOString().split("T")[0];
  const addLote = () => {
    const lastDate =
      data.length > 0 ? new Date(data[data.length - 1].fechaEnvio) : new Date();

    const nextDate = new Date(lastDate);
    if (data.length > 0) nextDate.setDate(nextDate.getDate() + 1);

    setData((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        fechaEnvio: formatDate(nextDate),
        numEnvios: Math.max(0, pendiente),
      },
    ]);
  };

  const removeLote = (id) => {
    setData((prev) => prev.filter((item) => item.id !== id));
  };

  const updateLote = (id, field, value) => {
    setData((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              [field]:
                field === "numEnvios" ? Math.max(0, Number(value)) : value,
            }
          : item,
      ),
    );
  };

  const distribuirEquitativamente = (numLotes) => {
    if (numLotes < 1) return;
    if (numLotes > total) numLotes = total;

    const base = Math.floor(total / numLotes);
    const resto = total % numLotes;

    const nuevosLotes = Array.from({ length: numLotes }, (_, i) => ({
      id: crypto.randomUUID(),
      fechaEnvio: "",
      numEnvios: base + (i < resto ? 1 : 0),
    }));

    setData(nuevosLotes);
    setTimeout(() => focusElement(LOTE_TAB_BASE), 0);
  };

  const todasFechasAsignadas =
    data.length > 0 && data.every((lote) => lote.fechaEnvio !== "");
  const isValid = pendiente === 0 && todasFechasAsignadas;

  useEffect(() => {
    focusElement(12);
  }, []);

  return (
    <div className="container-panel">
      <div style={{ marginBottom: 16 }}>
        <span>
          Total: <strong>{total}</strong>
        </span>
        <span style={{ margin: "0 8px" }}>|</span>
        <span>
          Asignados: <strong>{totalAsignado}</strong>
        </span>
        <span style={{ margin: "0 8px" }}>|</span>
        <span
          style={{
            color:
              pendiente === 0 ? "green" : pendiente > 0 ? "orange" : "#d81438",
          }}
        >
          Pendiente: <strong>{pendiente}</strong>
        </span>
      </div>

      <div className="opt-table-content grid-5" id="scheduled-send">
        <div className="cell">
          <ButtonIconH
            text="Añadir lote"
            small={true}
            onClick={addLote}
          ></ButtonIconH>
        </div>
        <div className="cell"></div>
        <div className="cell">Dividir en </div>
        <div className="cell">
          <InputData
            tabIndex={12}
            type="type-positive-integer"
            size={0}
            input={numBatches}
            onChange={(e) => {
              setNumBatches(e);
            }}
            onEnter={() => {
              distribuirEquitativamente(numBatches);
            }}
          ></InputData>
        </div>
        <div className="cell">
          <ButtonIconH
            text="Dividir"
            small={true}
            onClick={() => {
              distribuirEquitativamente(numBatches);
            }}
          ></ButtonIconH>
        </div>
      </div>

      {data.length > 0 && (
        <div
          className="dashboard-section"
          style={{
            maxHeight: 390,
            overflowY: "auto",
            marginTop: 20,
          }}
        >
          {data.map((lote, index) => {
            const tabFecha = LOTE_TAB_BASE + index * 2;
            const tabNum = LOTE_TAB_BASE + index * 2 + 1;
            const nextTab = LOTE_TAB_BASE + (index + 1) * 2;

            return (
              <div
                key={lote.id}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <span style={{ width: 20, fontWeight: 500 }}>{index + 1}.</span>
                <input
                  type="datetime-local"
                  max="9999-12-31T23:59"
                  className="input-field"
                  style={{ width: 180 }}
                  tabIndex={tabFecha}
                  value={lote.fechaEnvio}
                  onChange={(e) =>
                    updateLote(lote.id, "fechaEnvio", e.target.value)
                  }
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      focusElement(tabNum);
                    }
                  }}
                />
                <InputData
                  tabIndex={tabNum}
                  type="type-positive-integer"
                  size={1}
                  input={lote.numEnvios}
                  onChange={(e) => {
                    updateLote(lote.id, "numEnvios", e);
                  }}
                  onEnter={() => {
                    if (index < data.length - 1) {
                      focusElement(nextTab);
                    }
                  }}
                />
                <button type="button" onClick={() => removeLote(lote.id)}>
                  <img
                    src="/images/remove-blue.svg"
                    className="icon-pref"
                    alt="Eliminar producto"
                  />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!isValid && data.length > 0 && (
        <div
          style={{
            marginTop: 12,
            padding: 8,
            background: "#fff3cd",
            fontWeight: 500,
            borderRadius: 15,
          }}
        >
          {pendiente > 0
            ? `Faltan ${pendiente} por asignar`
            : pendiente < 0
              ? `Hay ${Math.abs(pendiente)} de más`
              : "Faltan fechas por asignar"}
        </div>
      )}

      {isValid && (
        <div
          style={{
            marginTop: 12,
            padding: 8,
            background: "#d4edda",
            borderRadius: 15,
            fontWeight: 500,
          }}
        >
          ✓ Distribución correcta
        </div>
      )}
      {isValid && (
        <div className="popup-buttons" style={{ marginTop: 20 }}>
          <ButtonIconH
            text="Aceptar"
            onClick={() => {
              onAccept(
                data.map((e) => ({
                  fechaHora: moment(e.fechaEnvio).format("YYYY-MM-DD HH:mm:ss"),
                  numEnvios: e.numEnvios,
                })),
              );
            }}
          ></ButtonIconH>
        </div>
      )}
    </div>
  );
}
